import { useMemo, useState } from "react";
import {
  PolicyIntervalType,
  PolicyType,
  IPolicy,
} from "shared";
import { When } from "react-if";

import { useCurrentGroup } from "../../../../hooks/useGroups";
import { Heading } from "../../../../../common/Atoms/Typography/Heading";
import { getDayIndex } from "../../../../lib/helper";
import { HelpIcon } from "../../../Molecules/HelpIcon";
import { Notification } from "../../../../../common/Atoms/Notification";
import { classNames } from "../../../../../common/lib/classNames";

import { ExistingPolicyItem } from "./ExistingPolicyItem";
import { AddNewPolicyCard } from "./AddNewPolicyCard";
import { FeatureInfoModal } from "./FeatureInfoModal";
import { featureInfoContent } from "./featureInfoContent";

interface AddNewOption {
  policyType: PolicyType;
  label: string;
  defaultOptions?: Partial<IPolicy>;
}

interface Subsection {
  title: string;
  filter: (policy: IPolicy) => boolean;
  addNewOptions: Array<AddNewOption>;
}

interface PolicyColumnProps {
  policyTypes: PolicyType[];
  title: string;
  description: string;
  addNewOptions?: Array<AddNewOption>;
  subsections?: Subsection[];
  duplicateWarning?: { title: string; body: string };
}

export function PolicyColumn({
  policyTypes,
  title,
  description,
  addNewOptions = [],
  subsections,
  duplicateWarning,
}: PolicyColumnProps) {
  const { policies } = useCurrentGroup();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const modalContent = featureInfoContent[title] || {
    description: description,
    benefits: [`Automate your workflow`, `Save time`, `Improve efficiency`],
  };

  const policiesOfType = useMemo(() => {
    if (!policies || !policies.length) {
      return [];
    }

    return policies
      .filter(x => policyTypes.includes(x.policy_type))
      .sort((a, b) => {
        const aDayIndex = getDayIndex(a);
        const bDayIndex = getDayIndex(b);

        return aDayIndex - bDayIndex;
      })
      // Then make sure the weeks are first for interval-based policies
      .sort((a, b) => {
        // Sort by if its week or month first. Then by day index. Put week first.
        const aIsMonthly = a.interval_type === PolicyIntervalType.MONTHLY || a.interval_type === PolicyIntervalType.MONTHLY_WEEKDAYS;
        const bIsMonthly = b.interval_type === PolicyIntervalType.MONTHLY || b.interval_type === PolicyIntervalType.MONTHLY_WEEKDAYS;

        if (aIsMonthly && b.interval_type === PolicyIntervalType.WEEKLY) {
          return 1;
        }

        if (a.interval_type === PolicyIntervalType.WEEKLY && bIsMonthly) {
          return -1;
        }

        return 0;
      });
  }, [policies, policyTypes]);

  // If none of this type and no add options, dont render
  if (!policiesOfType.length && !addNewOptions.length && !subsections?.length) {
    return null;
  }

  function renderAddNewOptions(options: AddNewOption[]) {
    const colCounts = {
      1: `grid-cols-1`,
      2: `grid-cols-2`,
      3: `grid-cols-3`,
    };

    return (
      <div className={
        classNames(
          `grid gap-2`,
          colCounts[Math.min(options.length, 3) as 1 | 2 | 3],
        )
      }>
        {
          options.map((option, idx) => (
            <AddNewPolicyCard
              key={ option.policyType + idx }
              policyType={ option.policyType }
              label={ option.label }
              defaultOptions={ option.defaultOptions }
            />
          ))
        }
      </div>
    );
  }

  // If we have subsections, render them
  if (subsections && subsections.length > 0) {
    return (
      <div className={ `w-full` }>
        { /* Main heading */ }
        <Heading className={ `mb-3` }>
          <span className={ `flex items-center` }>
            { title }
            <button
              onClick={ e => {
                e.stopPropagation();
                setIsModalOpen(true);
              } }
              className={ `ml-1 hover:opacity-70 transition-opacity` }
            >
              <HelpIcon tooltip={ description } />
            </button>
          </span>
        </Heading>

        <FeatureInfoModal
          isOpen={ isModalOpen }
          onClose={ () => setIsModalOpen(false) }
          title={ title }
          content={ modalContent }
        />

        <When condition={ !!duplicateWarning }>
          <Notification
            type={ `warning` }
            title={ duplicateWarning?.title }
            className={ `mb-4` }
          >
            { duplicateWarning?.body }
          </Notification>
        </When>

        { /* Render each subsection */ }
        <div className={ `space-y-4` }>
          { subsections.map((subsection, idx) => {
            const subsectionPolicies = policiesOfType.filter(subsection.filter);

            // Only render subsection if it has policies or add options
            if (subsectionPolicies.length === 0 && subsection.addNewOptions.length === 0) {
              return null;
            }

            return (
              <div key={ idx + subsection.title }
                className={ `space-y-2` }>
                <Heading variant={ `secondary` }
                  size={ `sm` }
                  className={ `text-gray-700` }>
                  { subsection.title }
                </Heading>
                <div className={ `space-y-2` }>
                  { subsectionPolicies.map(p => (
                    <ExistingPolicyItem key={ p.id }
                      policy={ p } />
                  )) }
                  { renderAddNewOptions(subsection.addNewOptions) }
                </div>
              </div>
            );
          }) }
        </div>
      </div>
    );
  }

  return (
    <div className={ `w-full` }>
      { /* Heading */ }
      <Heading
        className={ `mb-3` }
      >
        <span className={ `flex items-center` }>
          { title }
          <button
            onClick={ e => {
              e.stopPropagation();
              setIsModalOpen(true);
            } }
            className={ `ml-1 hover:opacity-70 transition-opacity` }
          >
            <HelpIcon
              tooltip={ description }
            />
          </button>
        </span>
      </Heading>

      <FeatureInfoModal
        isOpen={ isModalOpen }
        onClose={ () => setIsModalOpen(false) }
        title={ title }
        content={ modalContent }
      />

      <When condition={ !!duplicateWarning }>
        <Notification
          type={ `warning` }
          title={ duplicateWarning?.title }
          className={ `mb-4` }
        >
          { duplicateWarning?.body }
        </Notification>
      </When>

      { /*  List of existing policies */ }
      <div className={ `space-y-2` }>
        {
          policiesOfType.map(p => {
            return (
              <ExistingPolicyItem
                key={ p.id }
                policy={ p }
              />
            );
          })
        }

        { /* Add new policy cards */ }
        {
          renderAddNewOptions(addNewOptions)
        }
      </div>
    </div>
  );
}
